import { Text } from "@mantine/core";
import { useCallback, useMemo, useRef } from "react";
import { repositoryPos } from "../../../_base/_const/_constVar";
import ImportExcelShell from "../../../common/ImportExcel/ImportExcelShell";
import {
  ImportExcelColumn,
  ImportExcelRowErrors,
} from "../../../common/ImportExcel/ImportExcelTypes";
import { MessageResponse } from "../../../model/MessageResponse";

/** Serial tồn kho trả về từ TblInventorySerialOnHand/get-list */
type SerialOnHand = {
  maSp: string;
  maBarcode: string;
  tenSp: string;
  dvt: string;
};

const normalizeSerial = (value: any) => String(value ?? "").trim().toUpperCase();

const sampleRows = [
  {
    attribute2: "HMBH0224",
    attribute5: "SPF3ALNY8",
    productName: "",
    attribute3: "",
  },
  {
    attribute2: "",
    attribute5: "",
    productName: "Màn hình khách mang theo",
    attribute3: "Chiếc",
  },
];

/** Dựng dòng sản phẩm đúng cấu trúc listProduct của CreateDataViewV2 */
const toProductRow = (row: Record<string, any>) => {
  const isHacom = row.attribute4 === "HANG_HACOM";
  return {
    id: "0",
    orderHeaderId: null,
    orderDetailId: null,
    deliveryQuantity: 1,
    cancelledQuantity: null,
    shippedQuantity: null,
    productName: String(row.productName ?? "").trim(),
    lastUpdateDate: null,
    createBy: null,
    createDate: null,
    lastUpdateBy: null,
    deliveryId: null,
    // Hàng ngoài dùng serial giả 6 số giống nút "Thêm sản phẩm" để xoá được dòng, khi lưu sẽ bị bỏ
    attribute5: isHacom
      ? String(row.attribute5).trim()
      : `${Math.floor(100000 + Math.random() * 900000)}`,
    attribute2: String(row.attribute2 ?? "").trim() || null,
    attribute3: String(row.attribute3 ?? "").trim() || null,
    attribute4: isHacom ? "HANG_HACOM" : "HANG_NGOAI",
  };
};

/**
 * Import excel sản phẩm cho đơn giao hàng thủ công.
 * Dòng có serial là hàng Hacom: serial phải đang tồn kho, mã / tên / ĐVT lấy theo hệ thống.
 * Dòng không có serial là hàng ngoài: bắt buộc có tên sản phẩm.
 */
const ImportProductExcelView = ({
  existingSerials,
  onImported,
  onClose,
}: {
  /** Serial hàng Hacom đã có trong danh sách, để chặn thêm trùng */
  existingSerials: string[];
  /** Nhận các dòng sản phẩm hợp lệ để nối vào danh sách */
  onImported: (rows: any[]) => void;
  /** Đóng hộp import (Modal tự quản trong modal tạo đơn) */
  onClose: () => void;
}) => {
  const existingSet = useMemo(
    () => new Set(existingSerials.map(normalizeSerial)),
    [existingSerials]
  );
  /** Cache kết quả tra serial để sửa đi sửa lại không gọi api nhiều lần */
  const serialCacheRef = useRef<Map<string, Promise<SerialOnHand | null>>>(
    new Map()
  );

  const lookupSerial = (serial: string): Promise<SerialOnHand | null> => {
    const key = normalizeSerial(serial);
    const cached = serialCacheRef.current.get(key);
    if (cached) return cached;

    const promise = repositoryPos
      .get<MessageResponse<any>>(
        `/api/v1/TblInventorySerialOnHand/get-list?KeySearch=${encodeURIComponent(
          serial.trim()
        )}&Take=15`
      )
      .then((res) => {
        const list: any[] = res?.success && Array.isArray(res.data) ? res.data : [];
        const found = list.find((i) => normalizeSerial(i.maBarcode) === key);
        if (!found) {
          // Không tra được thì bỏ cache để lần sửa sau còn gọi lại
          serialCacheRef.current.delete(key);
          return null;
        }
        return {
          maSp: found.maSp,
          maBarcode: found.maBarcode,
          tenSp: found.tenSp,
          dvt: found.dvt,
        };
      });

    serialCacheRef.current.set(key, promise);
    return promise;
  };

  const columns = useMemo<ImportExcelColumn[]>(
    () => [
      {
        key: "attribute2",
        header: "Mã sản phẩm",
        size: 140,
        description: "Hàng Hacom tự lấy theo serial, hàng ngoài nhập nếu có",
      },
      {
        key: "attribute5",
        header: "Serial",
        size: 170,
        description:
          "Serial hàng Hacom đang tồn kho, để trống nếu là hàng ngoài",
      },
      {
        key: "productName",
        header: "Tên sản phẩm",
        size: 320,
        description: "Bắt buộc với hàng ngoài, hàng Hacom tự lấy theo serial",
      },
      {
        key: "attribute3",
        header: "Đơn vị tính",
        size: 110,
        description: "Hàng Hacom tự lấy theo serial",
      },
      {
        key: "loaiHang",
        header: "Loại hàng",
        size: 120,
        inTemplate: false,
        editable: false,
        description: "Hàng Hacom khi có serial, Hàng ngoài khi không có serial",
      },
    ],
    []
  );

  const mapRow = useCallback(
    (raw: Record<string, any>) => ({
      attribute2: String(raw.attribute2 ?? "").trim(),
      attribute5: String(raw.attribute5 ?? "").trim(),
      productName: String(raw.productName ?? "").trim(),
      attribute3: String(raw.attribute3 ?? "").trim(),
      attribute4: "",
      loaiHang: "",
      // Serial đã tra xong, lệch với serial đang nhập nghĩa là đang tra dở
      resolvedSerial: null as string | null,
    }),
    []
  );

  const resolveRow = useCallback(async (row: Record<string, any>) => {
    const serial = String(row.attribute5 ?? "").trim();

    if (serial.length === 0) {
      return {
        ...row,
        attribute4: "HANG_NGOAI",
        loaiHang: "Hàng ngoài",
        resolvedSerial: "",
      };
    }

    const found = await lookupSerial(serial);
    if (!found) {
      return { ...row, attribute4: "", loaiHang: "", resolvedSerial: serial };
    }

    return {
      ...row,
      attribute2: found.maSp,
      productName: found.tenSp,
      attribute3: found.dvt,
      attribute4: "HANG_HACOM",
      loaiHang: "Hàng Hacom",
      resolvedSerial: serial,
    };
  }, []);

  const validateRows = useCallback(
    (rows: Record<string, any>[]): ImportExcelRowErrors => {
      const errors: ImportExcelRowErrors = {};
      const serialSeen = new Map<string, number>();

      const addError = (index: number, key: string, message: string) => {
        if (!errors[index]) errors[index] = {};
        if (!errors[index][key]) errors[index][key] = message;
      };

      rows.forEach((row, index) => {
        const serial = String(row.attribute5 ?? "").trim();
        const name = String(row.productName ?? "").trim();

        if (serial.length === 0) {
          if (name.length === 0)
            addError(
              index,
              "productName",
              "Nhập serial (hàng Hacom) hoặc tên sản phẩm (hàng ngoài)"
            );
          return;
        }

        if (row.resolvedSerial !== serial)
          addError(index, "attribute5", "Đang tra serial trong kho, vui lòng đợi");
        else if (row.attribute4 !== "HANG_HACOM")
          addError(
            index,
            "attribute5",
            "Serial không có trong kho, kiểm tra lại hoặc bỏ serial để nhập hàng ngoài"
          );

        const key = normalizeSerial(serial);
        if (existingSet.has(key))
          addError(index, "attribute5", "Serial đã có trong danh sách sản phẩm");

        const first = serialSeen.get(key);
        if (first !== undefined)
          addError(index, "attribute5", `Trùng serial với dòng ${first + 1}`);
        else serialSeen.set(key, index);
      });

      return errors;
    },
    [existingSet]
  );

  const onSubmit = useCallback(
    async (rows: Record<string, any>[]) => {
      const items = rows.map(toProductRow);
      onImported(items);
      return {
        success: true,
        message: `Đã thêm ${items.length} sản phẩm vào danh sách !`,
      };
    },
    [onImported]
  );

  return (
    <ImportExcelShell
      columns={columns}
      templateFileName="mau-import-san-pham-don-giao-hang.xlsx"
      sampleRows={sampleRows}
      mapRow={mapRow}
      resolveRow={resolveRow}
      validateRows={validateRows}
      onSubmit={onSubmit}
      onClose={onClose}
      maxRow={200}
      submitLabel={(rowCount) => `Thêm ${rowCount} sản phẩm`}
      hint={
        <Text size="xs">
          Dòng có <b>Serial</b> là hàng Hacom: serial phải đang tồn kho, mã /
          tên / đơn vị tính sẽ lấy theo hệ thống. Dòng không có serial là hàng
          ngoài và bắt buộc nhập <b>Tên sản phẩm</b>. Dòng nào không hợp lệ sẽ
          báo đỏ để sửa ngay trên bảng xem trước.
        </Text>
      }
    />
  );
};

export default ImportProductExcelView;
